import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';

import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { FieldList } from '@/components/ui/field-list';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PeriodInput } from '@/components/ui/period-input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertCircle, Plus } from 'lucide-react';

import { TariffTypeEnum } from '@/graphql/generated';
import {
	useEntityForm,
	type EntityFormProps,
	type EntityFormSubmitHandler
} from '@/hooks/useEntityForm';
import { useTariffTypeLookup } from '@/hooks/useTariffTypeLookup';
import type { TariffFormSubmitData } from './tariff-input';

interface TariffStepForm {
	tariffStepId?: string;
	// stepSequence is computed from index on submit, not stored in form state
	intervalMinutes: number;
	intervalCharge: number;
	maxIntervals?: number;
}

// Form schema for validation - uses superRefine for conditional validation with specific error messages
// Note: stepSequence is NOT in the schema because it's computed from the array index during submission
const tariffFormSchema = z
	.object({
		name: z.string().min(1, 'Tariff name is required'),
		tariffTypeEnum: z.enum(['TIERED', 'FLAT_RATE'], {
			required_error: 'Tariff type is required'
		}),
		flatRateAmount: z.number().optional(),
		tariffSteps: z
			.array(
				z.object({
					tariffStepId: z.string().optional(),
					// stepSequence is intentionally omitted - computed from index on submit
					intervalMinutes: z
						.number({
							required_error: 'Interval is required',
							invalid_type_error: 'Interval must be a number'
						})
						.min(1, 'Interval must be at least 1 minute'),
					intervalCharge: z
						.number({
							required_error: 'Charge is required',
							invalid_type_error: 'Charge must be a number'
						})
						.min(0, 'Charge must be 0 or greater'),
					maxIntervals: z
						.number()
						.min(1, 'Max intervals must be at least 1')
						.optional()
				})
			)
			.optional()
	})
	.superRefine((data, ctx) => {
		// If TIERED, must have at least one step
		if (data.tariffTypeEnum === 'TIERED') {
			if (!data.tariffSteps || data.tariffSteps.length === 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						'At least one pricing step is required for tiered tariffs',
					path: ['tariffSteps']
				});
			}
		}
		// If FLAT_RATE, must have flatRateAmount > 0
		if (data.tariffTypeEnum === 'FLAT_RATE') {
			if (!data.flatRateAmount || data.flatRateAmount <= 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Flat rate amount must be greater than 0',
					path: ['flatRateAmount']
				});
			}
		}
	});

export type TariffFormValues = z.infer<typeof tariffFormSchema>;

export interface TariffFormProps
	extends Omit<
		EntityFormProps<TariffFormValues, TariffFormSubmitData>,
		'onSubmit'
	> {
	onSubmit: EntityFormSubmitHandler<TariffFormSubmitData>;
	headerTitle: string;
	onBack?: () => void;
}

const createEmptyStep = (): TariffStepForm => ({
	tariffStepId: undefined,
	intervalMinutes: 1,
	intervalCharge: 0,
	maxIntervals: undefined
});

const buildInitialValues = (data?: any): TariffFormValues | undefined => {
	if (!data) {
		return undefined;
	}

	return {
		name: data.name || '',
		tariffTypeEnum:
			data.tariffTypeEnum === TariffTypeEnum.FlatRate
				? 'FLAT_RATE'
				: 'TIERED',
		flatRateAmount: data.flatRateAmount || 0,
		tariffSteps: data.tariffSteps?.length
			? [...data.tariffSteps]
					.sort((a: any, b: any) => a.stepSequence - b.stepSequence)
					.map((step: any) => ({
						tariffStepId: step.tariffStepId,
						intervalMinutes: step.intervalMinutes || 1,
						intervalCharge: step.intervalCharge || 0,
						maxIntervals: step.maxIntervals
					}))
			: [createEmptyStep()]
	};
};

export const TariffForm = ({
	mode,
	initialData,
	onSubmit,
	isSubmitting,
	headerTitle,
	onBack
}: TariffFormProps) => {
	const { t } = useTranslation('tariffs');
	const { getTariffTypeTitle, isTieredTariff } = useTariffTypeLookup();
	const nameInputRef = useRef<HTMLInputElement>(null);

	// Auto-focus name field when creating a new tariff
	useEffect(() => {
		if (mode === 'create') {
			setTimeout(() => {
				nameInputRef.current?.focus();
			}, 0);
		}
	}, [mode]);

	const memoizedInitialData = useMemo(
		() => buildInitialValues(initialData),
		[initialData]
	);

	const form = useEntityForm<TariffFormValues>({
		mode,
		schema: tariffFormSchema,
		defaultValues: {
			name: '',
			tariffTypeEnum: 'TIERED',
			flatRateAmount: 0,
			tariffSteps: [createEmptyStep()]
		},
		initialData: memoizedInitialData
	});

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	const tariffTypeEnum = form.watch('tariffTypeEnum');
	const isTiered = isTieredTariff(tariffTypeEnum);

	const handleTariffTypeChange = (value: 'TIERED' | 'FLAT_RATE') => {
		// Don't validate immediately - let user fill in required fields first
		form.setValue('tariffTypeEnum', value, { shouldValidate: false });

		// Clear appropriate form data when switching types
		if (value === 'TIERED') {
			form.setValue('flatRateAmount', 0, { shouldValidate: false });
			const currentSteps = form.getValues('tariffSteps') || [];
			if (currentSteps.length === 0) {
				form.setValue('tariffSteps', [createEmptyStep()], {
					shouldValidate: false
				});
			}
		} else if (value === 'FLAT_RATE') {
			form.setValue('tariffSteps', [], { shouldValidate: false });
		}
	};

	const handleFormSubmit: EntityFormSubmitHandler<
		TariffFormValues
	> = values => {
		const submitData: TariffFormSubmitData = {
			name: values.name.trim(),
			tariffTypeEnum:
				values.tariffTypeEnum === 'TIERED'
					? TariffTypeEnum.Tiered
					: TariffTypeEnum.FlatRate
		};

		if (isTiered && values.tariffSteps) {
			submitData.tariffSteps = values.tariffSteps.map((step, index) => ({
				tariffStepId: step.tariffStepId,
				stepSequence: index + 1,
				intervalMinutes: step.intervalMinutes,
				intervalCharge: step.intervalCharge,
				maxIntervals: step.maxIntervals
			}));
		} else if (values.flatRateAmount) {
			submitData.flatRateAmount = values.flatRateAmount;
		}

		onSubmit(submitData);
	};

	let submitButtonText: string;
	if (mode === 'create') {
		submitButtonText = isSubmitting
			? t('form.creating')
			: t('form.createTariff');
	} else {
		submitButtonText = isSubmitting
			? t('form.saving')
			: t('form.saveChanges');
	}

	return (
		<>
			<FormHeaderWithStatus
				title={headerTitle}
				onBack={onBack}
				rightContent={
					<FormActionButtons
						onSubmit={() => form.handleSubmit(handleFormSubmit)()}
						isSubmitting={isSubmitting}
						submitLabel={submitButtonText}
						submitType="button"
						validationErrors={form.formState.errors}
						formId="tariff-form"
					/>
				}
			/>
			<Form {...form}>
			<form
				id="tariff-form"
				onSubmit={form.handleSubmit(handleFormSubmit)}
				className="space-y-4 lg:space-y-6"
			>
				{/* Basic Information */}
				<Card className="space-y-6">
					<div className="">
						<h3 className="text-base font-semibold text-gray-900 lg:text-lg dark:text-gray-100">
							{t('form.basicInformation')}
						</h3>
					</div>
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.tariffName')} *
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											ref={nameInputRef}
											placeholder={t(
												'form.tariffNamePlaceholder'
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="tariffTypeEnum"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.tariffType')} *
									</FormLabel>
									<FormControl>
										<RadioGroup
											value={field.value}
											onValueChange={
												handleTariffTypeChange
											}
											className="flex flex-row space-x-6"
										>
											<div className="flex items-center space-x-2">
												<RadioGroupItem
													value="TIERED"
													id="tiered"
												/>
												<Label
													htmlFor="tiered"
													className="text-sm cursor-pointer"
												>
													{getTariffTypeTitle(
														TariffTypeEnum.Tiered
													)}
												</Label>
											</div>
											<div className="flex items-center space-x-2">
												<RadioGroupItem
													value="FLAT_RATE"
													id="flatrate"
												/>
												<Label
													htmlFor="flatrate"
													className="text-sm cursor-pointer"
												>
													{getTariffTypeTitle(
														TariffTypeEnum.FlatRate
													)}
												</Label>
											</div>
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</Card>

				{/* Pricing Configuration - Tiered */}
				{isTiered && (
					<div className="space-y-4">
						<FieldList
							form={form}
							name="tariffSteps"
							className="space-y-4"
							itemsGridClassName="flex flex-col gap-4"
							title={t('form.pricingSteps')}
							description={t(
								'form.stepEmptyMessage',
								'Add a step to define pricing steps.'
							)}
							defaultItem={createEmptyStep()}
							idField="tariffStepId"
							forceHardDelete={true}
							addButtonLabel={t('form.addStep')}
							emptyMessage={t(
								'form.stepEmptyMessage',
								'Add a step to define pricing steps.'
							)}
							fields={[
								{
									name: 'intervalMinutes',
									isKeyField: true,
									colSpan: 'col-span-full',
									render: ({
										form,
										path,
										index,
										isNewItem
									}) => (
										<div className="space-y-4">
											{isNewItem && (
												<div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
													<Plus className="w-4 h-4" />
													<span>
														{t('form.addStep')}
													</span>
												</div>
											)}
											<div className="flex flex-wrap items-center gap-3 md:flex-nowrap">
												<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
													{t('form.chargeLabel')}
												</span>
												<FormField
													control={form.control}
													name={
														`${path}.intervalCharge` as any
													}
													render={({ field }) => (
														<FormItem className="flex-shrink-0 w-32">
															<FormControl>
																<Input
																	{...field}
																	type="number"
																	min={0}
																	step="0.01"
																	placeholder={t(
																		'form.intervalChargePlaceholder'
																	)}
																	onChange={e => {
																		const value =
																			e
																				.target
																				.value ===
																			''
																				? undefined
																				: Number.parseFloat(
																						e
																							.target
																							.value
																					);
																		field.onChange(
																			Number.isNaN(
																				value
																			)
																				? undefined
																				: value
																		);
																	}}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
													{t('form.perLabel')}
												</span>
												<FormField
													control={form.control}
													name={
														`${path}.intervalMinutes` as any
													}
													render={({ field }) => (
														<FormItem className="flex-shrink-0">
															<FormControl>
																<PeriodInput
																	value={
																		field.value
																	}
																	onChange={
																		field.onChange
																	}
																	aria-invalid={Boolean(
																		(
																			form
																				.formState
																				.errors
																				.tariffSteps as any
																		)?.[
																			index
																		]
																			?.intervalMinutes
																	)}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
													{t('form.minutesLabel')}
												</span>
												<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
													{t('form.forMaximumLabel')}
												</span>
												<FormField
													control={form.control}
													name={
														`${path}.maxIntervals` as any
													}
													render={({ field }) => (
														<FormItem className="flex-shrink-0 w-24">
															<FormControl>
																<Input
																	{...field}
																	type="number"
																	min={1}
																	placeholder={t(
																		'form.maxIntervalsPlaceholder'
																	)}
																	onChange={e => {
																		const value =
																			e
																				.target
																				.value ===
																			''
																				? undefined
																				: Number.parseInt(
																						e
																							.target
																							.value,
																						10
																					);
																		field.onChange(
																			value ===
																				undefined ||
																				Number.isNaN(
																					value
																				)
																				? undefined
																				: value
																		);
																	}}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
													{t('form.timesLabel')}
												</span>
											</div>
										</div>
									)
								}
							]}
							renderHeaderActions={({ index }) => (
								<div className="flex items-center justify-between w-full">
									<h4 className="text-base font-semibold text-gray-900 dark:text-gray-100">
										{t('form.step')} {index + 1}
									</h4>
								</div>
							)}
						/>
						{/* Display array-level validation error for tariffSteps */}
						{form.formState.errors.tariffSteps &&
							// Check for direct message (from superRefine) or root message (from useFieldArray rules)
							((!Array.isArray(
								form.formState.errors.tariffSteps
							) &&
								form.formState.errors.tariffSteps.message) ||
								(form.formState.errors.tariffSteps as any)?.root
									?.message) && (
								<Alert variant="destructive" className="mt-4">
									<AlertCircle className="w-4 h-4" />
									<AlertDescription>
										{(
											form.formState.errors
												.tariffSteps as any
										)?.message ||
											(
												form.formState.errors
													.tariffSteps as any
											)?.root?.message}
									</AlertDescription>
								</Alert>
							)}
					</div>
				)}

				{/* Pricing Configuration - Flat Rate */}
				{!isTiered && (
					<div className="">
						<hr className="mb-6 border-gray-200 dark:border-gray-700" />
						<div className="">
							<h3 className="text-base font-semibold text-gray-900 lg:text-lg dark:text-gray-100">
								{t('form.flatRatePricing')}
							</h3>
						</div>
						<div className="">
							<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
								<FormField
									control={form.control}
									name="flatRateAmount"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t('form.flatRateAmount')} *
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													type="number"
													step="0.01"
													min={0}
													placeholder={t(
														'form.flatRatePlaceholder'
													)}
													onChange={e =>
														field.onChange(
															Number.parseFloat(
																e.target.value
															) || 0
														)
													}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>
				)}
			</form>
		</Form>
		</>
	);
};
